<template>
  <UITable
    :columns="columns"
    :items="items"
    :loading="loading"
    :selectable="selectable"
    :empty-text="emptyText"
  >
    <!-- Custom Header Checkbox -->
    <template #head-checkbox>
      <TableCheckbox
        :disabled="isHeaderDisabled"
        :checked="isAllSelected"
        :indeterminate="isIndeterminate"
        @change="toggleSelectAll"
      />
    </template>

    <!-- Custom Body with Multi-level Rowspan & Grouped Hover -->
    <template #body>
      <!-- Loading State -->
      <tbody v-if="loading && (!items || items.length === 0)">
        <tr>
          <td
            :colspan="columns.length + (selectable ? 1 : 0)"
            class="py-8 text-center"
          >
            <div class="flex items-center justify-center gap-2">
              <b-spinner
                variant="primary"
                label="Loading..."
                small
              />
              <span class="text-sm text-[#737373]">Memuat data...</span>
            </div>
          </td>
        </tr>
      </tbody>

      <!-- Empty State -->
      <tbody v-else-if="!orderGroups.length">
        <tr>
          <td
            :colspan="columns.length + (selectable ? 1 : 0)"
            class="py-8 text-center text-[#737373] text-sm"
          >
            {{ emptyText }}
          </td>
        </tr>
      </tbody>

      <!-- Data Rows Grouped per Order: Pure CSS Hover without JS re-renders -->
      <tbody
        v-for="group in orderGroups"
        v-else
        :key="group.orderIndex"
        class="hover:bg-[#F9FAFB] transition-colors"
      >
        <tr
          v-for="(row, rIdx) in group.rows"
          :key="`${group.orderIndex}-${rIdx}`"
        >
          <!-- Checkbox (Merged per Order) -->
          <td
            v-if="selectable && row.isFirstOrderRow"
            :rowspan="row.orderRowspan"
            class="py-[13px] px-[16px] text-center align-middle border-b border-[#E5E5E5]"
          >
            <TableCheckbox
              :disabled="!isSelectableRow(row.order)"
              :indeterminate="!isSelectableRow(row.order)"
              :checked="selected.includes(row.orderIndex)"
              @change="() => toggleSelectRow(row.orderIndex)"
            />
          </td>

          <!-- No. Resi (Merged per Order) -->
          <td
            v-if="row.isFirstOrderRow"
            :rowspan="row.orderRowspan"
            class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px] break-all border-b border-[#E5E5E5]"
          >
            {{ row.order.awb || '-' }}
          </td>

          <!-- Nama Produk (Merged per Product, Auto-wrap safe) -->
          <td
            v-if="row.isFirstProductRow"
            :rowspan="row.productRowspan"
            class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px] break-words max-w-[280px] border-b border-[#E5E5E5]"
          >
            {{ row.productName }}
          </td>

          <!-- Variasi (Auto-wrap safe) -->
          <td class="py-[12px] px-[16px] align-middle text-[#0A0A0A] text-[14px] break-words max-w-[240px] border-b border-[#E5E5E5]">
            {{ row.variantName }}
          </td>

          <!-- Jumlah Item (Merged per Product) -->
          <td
            v-if="row.isFirstProductRow"
            :rowspan="row.productRowspan"
            class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px] border-b border-[#E5E5E5]"
          >
            {{ row.totalItem }}
          </td>

          <!-- Jumlah Order (Merged per Order) -->
          <td
            v-if="row.isFirstOrderRow"
            :rowspan="row.orderRowspan"
            class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px] border-b border-[#E5E5E5]"
          >
            {{ row.order.total_order !== undefined ? row.order.total_order : '-' }}
          </td>

          <!-- Status (Merged per Order) -->
          <td
            v-if="row.isFirstOrderRow"
            :rowspan="row.orderRowspan"
            class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px] border-b border-[#E5E5E5]"
          >
            <StatusPackingPill :status="row.order.status" />
          </td>

          <!-- Bahan Packing (Merged per Order) -->
          <td
            v-if="row.isFirstOrderRow"
            :rowspan="row.orderRowspan"
            class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px] border-b border-[#E5E5E5]"
          >
            <ul class="flex items-start justify-start flex-col gap-[4px] w-full shrink-0 mb-0">
              <li
                v-for="({ name, id, layer }, index) in row.order.packings || []"
                :key="`${id}-${name}-${index}`"
                class="flex-1 w-full"
              >
                <div class="flex items-center gap-2 justify-between">
                  <span>{{ name }}</span>
                  <span class="font-medium">x{{ layer }}</span>
                </div>
              </li>
            </ul>
          </td>
        </tr>
      </tbody>
    </template>
  </UITable>
</template>

<script>
import { Table as UITable } from '@/@core/components/ui'
import TableCheckbox from '@/@core/components/ui/table/TableCheckbox.vue'
import StatusPackingPill from '../../components/StatusPackingPill.vue'

export default {
  name: 'PackingDetailTable',
  components: {
    UITable,
    TableCheckbox,
    StatusPackingPill,
  },
  props: {
    batchStatus: {
      type: String,
      default: 'Belum Dipacking',
    },
    items: {
      type: Array,
      default: () => [],
    },
    grandTotalOrder: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: true,
    },
    emptyText: {
      type: String,
      default: 'Tidak ada data yang dapat ditampilkan.',
    },
  },
  data() {
    return {
      selected: [],
      columns: [
        { key: 'awb', label: 'No. Resi', thClass: 'min-w-[240px]' },
        { key: 'product_name', label: 'Nama Produk', thClass: 'min-w-[240px]' },
        { key: 'variant_name', label: 'Variasi', thClass: 'min-w-[200px]' },
        { key: 'total_item', label: 'Jumlah Item', thClass: 'min-w-[140px]' },
        { key: 'total_order', label: 'Jumlah Order', thClass: 'min-w-[140px]' },
        { key: 'status', label: 'Status', thClass: 'min-w-[160px]' },
        { key: 'packings', label: 'Bahan Packing', thClass: 'min-w-[180px]' },
      ],
    }
  },
  computed: {
    /**
     * Formats orders and nested products with rowspan metadata for table rendering.
     * @returns {Array<Object>}
     */
    orderGroups() {
      const { items } = this
      if (!Array.isArray(items) || items.length === 0) return []

      return items.map((order, orderIndex) => {
        const { products } = order

        // Fast-path: single standard product order without bundling (majority case)
        if (!products || products.length <= 1) {
          const prod = products && products.length > 0 ? products[0] : null
          const isBundling = Boolean(
            prod
            && prod.is_bundling
            && Array.isArray(prod.product_bundles)
            && prod.product_bundles.length > 0,
          )

          if (!isBundling) {
            let productName = '-'
            if (prod && prod.product_name) {
              productName = prod.product_name
            } else if (order.product_name) {
              productName = order.product_name
            }

            let variantName = '-'
            if (prod && prod.product_variant_name) {
              variantName = prod.product_variant_name
            } else if (order.product_variant_name) {
              variantName = order.product_variant_name
            } else if (order.variant_name) {
              variantName = order.variant_name
            }

            let totalItem = '-'
            if (prod && prod.total_item !== undefined) {
              totalItem = prod.total_item
            } else if (order.total_item !== undefined) {
              totalItem = order.total_item
            }

            return {
              orderIndex,
              order,
              rows: [{
                orderIndex,
                order,
                isFirstOrderRow: true,
                orderRowspan: 1,
                isFirstProductRow: true,
                productRowspan: 1,
                productName,
                variantName,
                totalItem,
              }],
            }
          }
        }

        const totalOrderRows = products.reduce((acc, prod) => {
          const isBundling = Boolean(
            prod
            && prod.is_bundling
            && Array.isArray(prod.product_bundles)
            && prod.product_bundles.length > 0,
          )
          const count = isBundling ? prod.product_bundles.length : 1
          return acc + count
        }, 0)

        const rows = []
        let orderRowCounter = 0

        products.forEach(prod => {
          const isBundling = Boolean(
            prod
            && prod.is_bundling
            && Array.isArray(prod.product_bundles)
            && prod.product_bundles.length > 0,
          )
          const bundles = isBundling ? prod.product_bundles : [null]
          const productRowspan = bundles.length
          const productName = prod.product_name || '-'
          const totalItem = prod.total_item !== undefined ? prod.total_item : '-'

          bundles.forEach((bundle, bIdx) => {
            let variantName = prod.product_variant_name || '-'
            if (isBundling && bundle) {
              variantName = bundle.product_variant_name || bundle.product_name || bundle.name || '-'
            }

            rows.push({
              orderIndex,
              order,
              isFirstOrderRow: orderRowCounter === 0,
              orderRowspan: totalOrderRows,
              isFirstProductRow: bIdx === 0,
              productRowspan,
              productName,
              variantName,
              totalItem,
            })
            orderRowCounter += 1
          })
        })

        return {
          orderIndex,
          order,
          rows,
        }
      })
    },
    selectableOrders() {
      if (!Array.isArray(this.items)) return []
      return this.items.filter(item => this.isSelectableRow(item))
    },
    isHeaderDisabled() {
      return !Array.isArray(this.items) || this.items.length === 0 || this.selectableOrders.length === 0
    },
    isAllSelected() {
      const totalSelectable = this.selectableOrders.length
      return totalSelectable > 0 && this.selected.length >= totalSelectable
    },
    isIndeterminate() {
      if (this.isHeaderDisabled && Array.isArray(this.items) && this.items.length > 0) {
        return true
      }
      const totalSelectable = this.selectableOrders.length
      const count = this.selected.length
      return count > 0 && count < totalSelectable
    },
  },
  watch: {
    items: {
      handler() {
        this.selected = []
        this.$emit('update-selected', [])
      },
    },
  },
  methods: {
    /**
     * Checks if an order row is eligible for selection.
     * @param {Object} item - Order item object.
     * @returns {boolean}
     */
    isSelectableRow(item) {
      if (this.batchStatus === 'Dibatalkan' || this.batchStatus === 'Dikirim') {
        return false
      }
      return Boolean(item && item.status !== 'Dibatalkan' && item.status !== 'Dikirim')
    },
    /**
     * Toggles selection for all eligible order items.
     * @param {boolean} checked
     */
    toggleSelectAll(checked) {
      if (this.isHeaderDisabled) return
      const validIndexes = checked && Array.isArray(this.items)
        ? this.items.reduce((acc, item, idx) => {
          if (this.isSelectableRow(item)) acc.push(idx)
          return acc
        }, [])
        : []
      this.selected = validIndexes
      this.$emit('update-selected', validIndexes)
    },
    /**
     * Toggles selection state for an individual order by index.
     * @param {number} orderIndex
     */
    toggleSelectRow(orderIndex) {
      const idx = this.selected.indexOf(orderIndex)
      if (idx > -1) {
        this.selected.splice(idx, 1)
      } else {
        this.selected.push(orderIndex)
      }
      this.$emit('update-selected', this.selected)
    },
    getSelectedItems() {
      return [...this.selected]
    },
    clearSelection() {
      this.selected = []
      this.$emit('update-selected', [])
    },
  },
}
</script>
